import React, { useEffect, useRef, useState } from 'react';

import '../../../components/modal/contac/filtro/ModalFiltro.css';
import '../pages/DashboardCompras.css';
import '../../../shared/feedback/aviso-de-tela.css';

/**
 * Seleção de vários valores, em modal com caixas de seleção (at-112).
 *
 * Substitui os menus suspensos de status dos cards e o de setor do filtro geral, que aceitavam um
 * valor só. O modal é o padrão do projeto para isto (`03-code-conventions.md`: ações por linha
 * ficam em modal, e o menu suspenso foi recusado pelo Victor no Portal) — e aqui ele resolve o que
 * um `select multiple` não resolve: cabe a contagem ao lado de cada opção, cabe busca, e a escolha
 * sai em bloco, numa requisição só.
 *
 * 🔴 **A seleção só sai no "Aplicar".** O estado é local e o "Cancelar" desfaz de verdade — sem
 * isso, marcar cinco centros de custo dispararia cinco recargas do painel.
 *
 * ⚠️ **Nada marcado significa TODOS**, e o rodapé diz isso por extenso. É a leitura natural de uma
 * lista de caixas vazias ("não filtrei"), mas a oposta também é plausível ("não quero nenhum") — e
 * num filtro a diferença é entre ver a base inteira e ver a tela vazia.
 */

export interface OpcaoDeSelecao {
  valor: string;
  rotulo: string;
  /** Texto secundário na mesma linha (código do ERP, contagem). Nunca quebra a linha. */
  detalhe?: string;
}

interface Props {
  titulo: string;
  opcoes: OpcaoDeSelecao[];
  selecionados: string[];
  onAplicar: (selecionados: string[]) => void;
  onFechar: () => void;
  /** Aparece acima da lista quando há algo a declarar (cadastro vazio, carga que falhou). */
  aviso?: string;
  /** Liga o campo de busca. Vale a pena a partir de ~10 opções. */
  comBusca?: boolean;
  /**
   * Aberto POR CIMA de outro modal (o de Filtros). Sobe o z-index do overlay: com o mesmo valor do
   * pai, quem aparece por cima é decidido pela ordem do DOM — o `BUG-R97`. A outra metade da
   * receita (o pai ignorar clique e Escape enquanto este estiver aberto) é do pai.
   */
  empilhado?: boolean;
  sidebarRef?: React.RefObject<HTMLElement>;
}

export default function SelecaoMultiplaModal({
  titulo,
  opcoes,
  selecionados,
  onAplicar,
  onFechar,
  aviso,
  comBusca = false,
  empilhado = false,
  sidebarRef,
}: Props) {
  // Montagem condicional no pai (padrão do projeto): o estado inicial é a única fonte, e cada
  // abertura recomeça do que está aplicado — é o que fecha o BUG-V43.
  const [escolhidos, setEscolhidos] = useState<string[]>(selecionados);
  const [busca, setBusca] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const aoClicarFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      if (ref.current?.contains(alvo)) return;
      if (sidebarRef?.current?.contains(alvo)) return;
      onFechar();
    };
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };
    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [onFechar, sidebarRef]);

  const alternar = (valor: string) =>
    setEscolhidos((atual) =>
      atual.includes(valor) ? atual.filter((v) => v !== valor) : [...atual, valor],
    );

  const termo = busca.trim().toLowerCase();
  const visiveis = termo
    ? opcoes.filter((o) => `${o.rotulo} ${o.detalhe ?? ''}`.toLowerCase().includes(termo))
    : opcoes;

  return (
    <div className={`modal-filtro-overlay${empilhado ? ' compras-overlay-empilhado' : ''}`}>
      {/* Três níveis, como o molde do Contac — ver a nota no FiltroComprasModal. */}
      <div className="modal-filtro-content" ref={ref}>
        <div className="filtro-modal-card">
          <div className="filtro-modal-header">
            <h2>{titulo}</h2>
            <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
              ×
            </button>
          </div>

          <div className="filtro-modal-body">
            {aviso ? <p className="aviso-de-tela">{aviso}</p> : null}
            {comBusca ? (
              <input
                type="text"
                aria-label="Buscar"
                className="premium-text-input compras-checkbox-busca"
                placeholder="Buscar..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            ) : null}

            <div className="compras-checkbox-lista" role="group" aria-label={titulo}>
              {visiveis.length === 0 ? (
                <p className="filtro-field-hint">Nenhuma opção corresponde à busca.</p>
              ) : (
                visiveis.map((o) => (
                  <label key={o.valor} className="compras-checkbox-item">
                    <input
                      type="checkbox"
                      checked={escolhidos.includes(o.valor)}
                      onChange={() => alternar(o.valor)}
                    />
                    <span>{o.rotulo}</span>
                    {o.detalhe ? <span className="compras-checkbox-detalhe">{o.detalhe}</span> : null}
                  </label>
                ))
              )}
            </div>

            <p className="filtro-field-hint">
              {escolhidos.length === 0
                ? 'Nada marcado: o painel mostra todos.'
                : `${escolhidos.length} de ${opcoes.length} marcados.`}
            </p>
          </div>

          <div className="filtro-modal-footer">
            <button
              type="button"
              className="filtro-btn-secondary filtro-btn-limpar"
              onClick={() => setEscolhidos([])}
            >
              Limpar seleção
            </button>
            <button type="button" className="filtro-btn-secondary" onClick={onFechar}>
              Cancelar
            </button>
            <button type="button" className="filtro-btn-primary" onClick={() => onAplicar(escolhidos)}>
              Aplicar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
